package com.zaurh.mapquizwebsite.presentation.screen.main

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.tween
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.material3.DrawerValue
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.rememberDrawerState
import androidx.compose.runtime.*
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.navigation.compose.rememberNavController
import com.zaurh.mapquizmobile.presentation.navigation.NavGraph
import com.zaurh.mapquizmobile.presentation.navigation.Screen
import com.zaurh.mapquizmobile.ui.theme.MapQuizTheme
import com.zaurh.mapquizwebsite.presentation.screen.home.MainAction
import com.zaurh.mapquizwebsite.presentation.screen.home.MainSideEffect
import com.zaurh.mapquizwebsite.presentation.screen.main.components.SignInAlert
import com.zaurh.mapquizwebsite.presentation.screen.main.left.MainScreenLeft
import com.zaurh.mapquizwebsite.presentation.screen.main.right.MainScreenRight
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource

import mapquizwebsite.shared.generated.resources.Res
import mapquizwebsite.shared.generated.resources.ic_menu
import mapquizwebsite.shared.generated.resources.ic_news
import org.koin.compose.viewmodel.koinViewModel

@Composable
@Preview
fun App() {
    MapQuizTheme(darkTheme = false) {
        val viewModel: MainViewModel = koinViewModel()
        val uiState by viewModel.uiState.collectAsState()
        val navController = rememberNavController()
        val scope = rememberCoroutineScope()

        val leftDrawerState = rememberDrawerState(
            initialValue = DrawerValue.Closed
        )

        val rightDrawerState = rememberDrawerState(
            initialValue = DrawerValue.Closed
        )

        LaunchedEffect(Unit){
            viewModel.onAction(MainAction.OnInit)
        }

        LaunchedEffect(Unit){
            viewModel.sideEffect.collect {
                when(it){
                    MainSideEffect.NavigateToDailyRewards -> {
                        scope.launch {
                            leftDrawerState.close()
                        }
                        navController.navigate(Screen.DailyRewardsScreen.route)
                    }

                    MainSideEffect.NavigateToHome -> {
                        scope.launch {
                            leftDrawerState.close()
                        }
                        navController.navigate(Screen.HomeScreen.route)
                    }
                }
            }
        }

        BoxWithConstraints {
            val maxWidth = maxWidth

            LaunchedEffect(maxWidth){
                if (maxWidth > 1260.dp){
                    leftDrawerState.close()
                    rightDrawerState.close()
                }
            }

            val isMobile = maxWidth < 600.dp


            Row(modifier = Modifier.fillMaxSize()) {
                if (maxWidth > 1260.dp) {
                    MainScreenLeft(
                        modifier = Modifier.weight(0.25f),
                        onHomeClicked = {
                            viewModel.onAction(MainAction.OnHomeClicked)
                        },
                        onDailyRewardClicked = {
                            viewModel.onAction(MainAction.OnDailyRewardsClicked)
                        }
                    )
                }
                Column(modifier = Modifier.weight(1f)) {
                    SignInAlert(
                        isVisible = uiState.isSignInAlertVisible,
                        value = uiState.signInUsername,
                        onDismiss = { viewModel.onAction(MainAction.OnSignInAlertDismissed) },
                        onValueChange = {
                            viewModel.onAction(MainAction.OnSignInUsernameChange(it))
                        },
                        onSignInClicked = {
                            viewModel.onAction(MainAction.OnSignInAlertConfirmed)
                        }
                    )

                    if (maxWidth < 1260.dp) {
                        Row(
                            modifier = Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.surface),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            IconButton(
                                onClick = {
                                    scope.launch {
                                        if (leftDrawerState.isOpen){
                                            leftDrawerState.close()
                                            return@launch
                                        }else {
                                            leftDrawerState.open()
                                            rightDrawerState.close()
                                            return@launch
                                        }
                                    }
                                }
                            ) {
                                Icon(
                                    painter = painterResource(Res.drawable.ic_menu),
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.primary,
                                    modifier = Modifier.size(24.dp)
                                )
                            }
                            IconButton(
                                onClick = {
                                    scope.launch {
                                        if (rightDrawerState.isOpen){
                                            rightDrawerState.close()
                                            return@launch
                                        }else {
                                            rightDrawerState.open()
                                            leftDrawerState.close()
                                            return@launch
                                        }
                                    }
                                }
                            ) {
                                Icon(
                                    painter = painterResource(Res.drawable.ic_news),
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.primary
                                )
                            }
                        }
                    }
                    NavGraph(viewModel = viewModel, navController = navController)
                }

                if (maxWidth > 1260.dp) {
                    MainScreenRight(
                        modifier = Modifier.weight(0.25f),
                        uiState = uiState,
                        onSignInClicked = {
                            viewModel.onAction(MainAction.OnSignInClicked)
                        },
                        onLogOutClicked = {
                            viewModel.onAction(MainAction.OnLogOutClicked)
                        }
                    )
                }
            }

            val scrimColor by animateColorAsState(
                targetValue = if (leftDrawerState.isOpen || rightDrawerState.isOpen) {
                    Color.Black.copy(alpha = 0.32f)
                } else {
                    Color.Transparent
                },
                animationSpec = tween(200)
            )

            if (leftDrawerState.isOpen || rightDrawerState.isOpen){
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(scrimColor)
                        .clickable(
                            indication = null,
                            interactionSource = remember { MutableInteractionSource() }
                        ) {
                            scope.launch {
                                leftDrawerState.close()
                                rightDrawerState.close()
                            }
                        }
                )
            }

            AnimatedVisibility(
                visible = leftDrawerState.isOpen,
                modifier = Modifier
                    .fillMaxHeight()
                    .align(Alignment.CenterStart),
                enter = slideInHorizontally { -it },
                exit = slideOutHorizontally { -it }
            ) {
                MainScreenLeft(
                    modifier = Modifier
                        .fillMaxHeight()
                        .fillMaxWidth(0.7f),
                    onHomeClicked = {
                        viewModel.onAction(MainAction.OnHomeClicked)
                    },
                    onDailyRewardClicked = {
                        viewModel.onAction(MainAction.OnDailyRewardsClicked)
                    }
                )
            }

            AnimatedVisibility(
                visible = rightDrawerState.isOpen,
                modifier = Modifier
                    .fillMaxHeight()
                    .align(Alignment.CenterEnd),
                enter = slideInHorizontally { it },
                exit = slideOutHorizontally { it }
            ) {
                MainScreenRight(
                    modifier = Modifier
                        .fillMaxHeight()
                        .fillMaxWidth(0.7f),
                    uiState = uiState,
                    onSignInClicked = {
                        viewModel.onAction(MainAction.OnSignInClicked)
                    },
                    onLogOutClicked = {
                        viewModel.onAction(MainAction.OnLogOutClicked)
                    }
                )
            }


        }
    }
}